<template>
  <div class="image-picker">
    <input
      ref="filePickerEl"
      type="file"
      hidden
      accept="image/*"
      @change="upload"
    >

    <div class="buttons-y">
      <button
        type="button"
        @click.passive="openPicker()"
      >
        <IconPlus v-if="hasBackgroundImage === false"/>
        <IconRestart v-else/>
        <span>{{ hasBackgroundImage ? "Vervang je foto" : "Kies je foto" }}</span>
      </button>
    </div>

    <p
      v-if="backgroundImageFile !== null"
      class="image-picker__filename"
    >
      {{ backgroundImageFile.name }}
    </p>

    <template v-if="hasBackgroundImage">
      <strong class="image-picker__background-image-scale-title">Afbeelding schalen</strong>
      <input
        id="image-picker__background-image-scale-slider"
        v-model="backgroundImageScale"
        type="range"
        :min="minBackgroundImageScale"
        :max="maxBackgroundImageScale"
        :step="1 / backgroundImageScaleRoundFactor"
        :disabled="hasBackgroundImageQualityProblem || (maxBackgroundImageScale <= minBackgroundImageScale)"
      >
      <output
        for="image-picker__background-image-scale-slider"
        class="output--tooltip"
      >
        {{ backgroundImageScale.toFixed(1).replace('.', ',') }}x
      </output>

      <div
        v-if="hasBackgroundImageQualityProblem"
        class="notification notification--small notification--warn image-picker__quality-warning"
      >
        <IconWarning/>
        <div class="notification__message">
          Je hebt de achtergrondafbeelding teveel uitvergroot of de afbeelding is niet groot genoeg om scherp weer te geven. Upload een grotere afbeelding of vergroot hem zo mogelijk minder uit.
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, useTemplateRef, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import IconPlus from '@/components/svg/IconPlus.vue';
  import IconRestart from '@/components/svg/IconRestart.vue';
  import IconWarning from '@/components/svg/IconWarning.vue';
  import { backgroundImageScaleRoundFactor, useBackgroundStore } from '@/stores/useBackgroundStore';
  import { useImagesStore } from '@/stores/useImagesStore';
  import { useToaster } from '@/composables/useToaster';

  const backgroundStore = useBackgroundStore();
  const imagesStore = useImagesStore();
  const toaster = useToaster();

  const {
    hasBackgroundImage,
    hasBackgroundImageQualityProblem,
    minBackgroundImageScale,
    maxBackgroundImageScale,
    backgroundImageFile,
  } = storeToRefs(backgroundStore);

  const filePickerEl = useTemplateRef('filePickerEl');

  const backgroundImageScale = computed({
    get: () => backgroundStore.backgroundImageScale,
    set: (value) => {
      // The slider value is a string at runtime.
      backgroundStore.setBackgroundImageScale(Number(value as unknown as string));
    },
  });

  watch(() => backgroundStore.imagePickerTriggerCount, () => {
    openPicker();
  });

  function openPicker () {
    filePickerEl.value?.click();
  }

  function upload () {
    const files = filePickerEl.value?.files;

    if (files == null || files.length === 0) {
      toaster.error('Selecteer minstens één foto om deze te gebruiken.');
      return;
    }

    const [file] = files;

    if (file == null) {
      toaster.error('Selecteer minstens één foto om deze te gebruiken.');
      return;
    }

    imagesStore.pipeFileAsBase64To({
      file,
      handler: backgroundStore.setBackgroundImage,
    });
  }
</script>

<style lang="scss">
  .image-picker {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__filename {
      font-size: 13px;
      line-height: 1.4;
      color: color-opacity(--color-black, 55%);
      overflow-wrap: anywhere;
    }

    &__background-image-scale-title {
      @include ui-headings-font;

      font-family: var(--ui-default-sub-headings-font-family);
      font-size: 18px;
      line-height: 1.2;
      margin-top: 0.5em;
    }

    &__quality-warning {
      margin: 0;
      align-items: flex-start;

      svg {
        flex: 0 0 auto;
        width: 18px;
        height: 18px;
        margin-top: 2px;
      }
    }
  }
</style>
